import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Typography,
  Breadcrumbs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import Link from "@mui/material/Link";
import axios from "axios";
import moment from "moment";
import Layout from "../../layout/Layout";
import { AlertMessage } from "../../components/AlertMessage";
import { config, paymentApproverSettingsAPI, userData } from "../../config";

// Global, person-based verifier/approver/payer for payment requests. Mirrors
// the labour approver settings (LabourApproverSettings.jsx). All calls hit the
// dedicated POST /payment-request/settings endpoint (Node SQL, not the SP):
//   16 get config · 17 set config (+ history) · 21 verifier options
//   22 approver options · 23 assignment history · 24 payer options
function PaymentApproverSettings() {
  const childRef = useRef();

  // Verifier options = active accounts-team people (intType 21).
  // Approver options = active super admins (intType 22).
  // Payer options    = active accountants, role 35 (intType 24).
  const [verifierList, setVerifierList] = useState([]);
  const [approverList, setApproverList] = useState([]);
  const [payerList, setPayerList] = useState([]);
  const [verifierID, setVerifierID] = useState(0);
  const [approverID, setApproverID] = useState(0);
  const [payerID, setPayerID] = useState(0);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const isSuperAdmin = Number(userData?.role_id) === 34;

  // One person may legitimately hold two duties (e.g. verify and pay); the
  // chain still runs stage by stage, so we only flag it, never block the save.
  const sameVerifierPayer =
    verifierID > 0 && Number(verifierID) === Number(payerID);

  let tempRefs = useRef(false);
  useEffect(() => {
    if (tempRefs.current === false && isSuperAdmin) {
      fetchOptions(21, setVerifierList);
      fetchOptions(22, setApproverList);
      fetchOptions(24, setPayerList);
      fetchApproverConfig();
      fetchHistory();
      tempRefs.current = true;
    }
  }, []);

  const fetchHistory = () => {
    config.method = "POST";
    config.data = JSON.stringify({ intType: 23 });
    config.url = paymentApproverSettingsAPI;
    axios(config)
      .then((response) => setHistory(response.data.result || []))
      .catch((error) => console.log(error));
  };

  const fetchOptions = (intType, setter) => {
    config.method = "POST";
    config.data = JSON.stringify({ intType });
    config.url = paymentApproverSettingsAPI;

    axios(config)
      .then(function (response) {
        setter(response.data.result || []);
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  const fetchApproverConfig = () => {
    config.method = "POST";
    config.data = JSON.stringify({ intType: 16 });
    config.url = paymentApproverSettingsAPI;

    axios(config)
      .then(function (response) {
        const cfg = response.data.result && response.data.result[0];
        if (cfg) {
          setVerifierID(Number(cfg.verifier_emp_id) || 0);
          setApproverID(Number(cfg.approver_emp_id) || 0);
          setPayerID(Number(cfg.payer_emp_id) || 0);
        }
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  const handleSave = () => {
    setLoading(true);
    config.method = "POST";
    config.data = JSON.stringify({
      intType: 17,
      intVerifierID: verifierID,
      intApproverID: approverID,
      intPayerID: payerID,
      intUserID: userData.emp_id,
    });
    config.url = paymentApproverSettingsAPI;

    axios(config)
      .then(function (response) {
        setLoading(false);
        if (response.data.success) {
          childRef.current.triggerAlert(
            JSON.stringify({
              message: response.data.message,
              messageType: "success",
            })
          );
          fetchApproverConfig();
          fetchHistory();
        } else {
          childRef.current.triggerAlert(
            JSON.stringify({
              message: response.data.message || "Something went wrong",
              messageType: "error",
            })
          );
        }
      })
      .catch(function (error) {
        setLoading(false);
        console.log(error);
        childRef.current.triggerAlert(
          JSON.stringify({
            message: "Something went wrong",
            messageType: "error",
          })
        );
      });
  };

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3}>
          <Grid
            textAlign="center"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <Typography className="page_title" textAlign="left">
              Payment Approver Settings
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">
                Payment Approver Settings
              </Typography>
            </Breadcrumbs>
          </Grid>
        </Grid>

        <div className="empty-space-40"></div>

        {!isSuperAdmin ? (
          <Typography color="error" sx={{ mt: 2 }}>
            You are not authorized to view this page.
          </Typography>
        ) : (
          <Grid
            container
            spacing={2}
            margin={`15px 0px`}
            padding={`10px 15px 0px 0px`}
          >
            <Grid item xs={12} lg={4} md={4}>
              <FormControl fullWidth>
                <InputLabel id="verifier-select-label">
                  Payment request Verifier
                </InputLabel>
                <Select
                  labelId="verifier-select-label"
                  id="verifier-select"
                  value={verifierID}
                  label="Payment request Verifier"
                  onChange={(e) => setVerifierID(e.target.value)}
                >
                  <MenuItem value={0}>
                    <em>None</em>
                  </MenuItem>
                  {verifierList.map((row) => (
                    <MenuItem value={row.emp_id} key={row.emp_id}>
                      {row.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} lg={4} md={4}>
              <FormControl fullWidth>
                <InputLabel id="approver-select-label">
                  Payment request Approver
                </InputLabel>
                <Select
                  labelId="approver-select-label"
                  id="approver-select"
                  value={approverID}
                  label="Payment request Approver"
                  onChange={(e) => setApproverID(e.target.value)}
                >
                  <MenuItem value={0}>
                    <em>None</em>
                  </MenuItem>
                  {approverList.map((row) => (
                    <MenuItem value={row.emp_id} key={row.emp_id}>
                      {row.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} lg={4} md={4}>
              <FormControl fullWidth>
                <InputLabel id="payer-select-label">
                  Payment request Payer
                </InputLabel>
                <Select
                  labelId="payer-select-label"
                  id="payer-select"
                  value={payerID}
                  label="Payment request Payer"
                  onChange={(e) => setPayerID(e.target.value)}
                >
                  <MenuItem value={0}>
                    <em>Any accountant</em>
                  </MenuItem>
                  {payerList.map((row) => (
                    <MenuItem value={row.emp_id} key={row.emp_id}>
                      {row.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", mt: 0.5, display: "block" }}
              >
                {payerID
                  ? "Only this person can mark approved requests as paid."
                  : "Not assigned — any active accountant can mark requests paid."}
              </Typography>
            </Grid>

            {sameVerifierPayer && (
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: "#8a6d1f" }}>
                  The same person verifies and pays. That is allowed — the
                  request still stops at each stage, and both duties are listed
                  separately in the app.
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <LoadingButton
                size="large"
                variant="contained"
                color="primary"
                loading={loading}
                onClick={handleSave}
              >
                Save
              </LoadingButton>
            </Grid>
          </Grid>
        )}

        {isSuperAdmin && (
          <>
            <div className="empty-space-40"></div>
            <Typography sx={{ fontWeight: 700, mb: 1.5, color: "#334" }}>
              Assignment history
            </Typography>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 2, maxWidth: 900 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f4f6fb" }}>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Verifier</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Approver</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Payer</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Changed by</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                      Date &amp; time
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        align="center"
                        sx={{ py: 3, color: "text.secondary" }}
                      >
                        No assignments yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    history.map((h, i) => (
                      <TableRow key={h.id} hover>
                        <TableCell>
                          {i === 0 ? (
                            <Chip size="small" color="success" label="Current" />
                          ) : (
                            history.length - i
                          )}
                        </TableCell>
                        <TableCell>{h.verifier_name || "—"}</TableCell>
                        <TableCell>{h.approver_name || "—"}</TableCell>
                        <TableCell>{h.payer_name || "Any accountant"}</TableCell>
                        <TableCell>{h.updated_by_name || "—"}</TableCell>
                        <TableCell align="right">
                          {h.created_at
                            ? moment(h.created_at).format("DD MMM YYYY, hh:mm A")
                            : "—"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Grid>
      <AlertMessage ref={childRef} />
    </Layout>
  );
}

export default PaymentApproverSettings;
